<template>
  <div class="event-view-wrapper" v-if="!loading" :style="eventColorVariables">
    <v-btn style="position: fixed; top: 10px; z-index: 5; left: 10px" x-large icon @click="$router.push('/')">
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>
    <div class="header-wrapper">
      <v-container class="content-container py-0">
        <v-img :src="headerImage" aspect-ratio="2.9" cover class="event-header-image rounded-xl">
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </v-container>
    </div>

    <div class="content-section">
      <v-container class="content-container">
        <div class="text-center mt-6 mb-4">
          <h2 class="section-title-pill d-inline-block font-weight-bold text-uppercase">🗓️ Kurse & Termine</h2>
        </div>

        <v-slide-y-transition v-if="courseExecutions.length > 0" group tag="v-row" class="d-flex flex-wrap">
          <v-col cols="12" md="6" v-for="course in courseExecutions" :key="course._id">
            <v-card class="course-card rounded-xl elevation-6" @click="pushToCourse(course)">
              <v-img
                class="rounded-t-xl"
                :src="getCourseImage(course.course.bild)"
                aspect-ratio="2"
                cover
                gradient="to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.5) 100%">
                <div class="date-badge">
                  <span class="day">{{ timeService.timestampToDay(course.ts) }}</span>
                  <span class="month" :style="{ color: event?.secondaryColor || '#00963f' }">
                    {{ timeService.timestampToMonth(course.ts) }}
                  </span>
                </div>
              </v-img>

              <div class="card-content pa-4">
                <h3 class="course-title font-weight-bold text-grey-darken-3 mb-3">
                  {{ course.course.name }}
                </h3>

                <div class="info-pills">
                  <div class="info-pill">
                    <v-icon left small>mdi-clock-outline</v-icon>
                    <span>{{ timeService.getCourseStartTime(course) }} - {{ timeService.getCourseEndTime(course) }} Uhr</span>
                  </div>
                  <div class="info-pill duration-pill">
                    <v-icon left small>mdi-timer-outline</v-icon>
                    <span>{{ course.duration }} Min.</span>
                  </div>
                </div>
              </div>

              <div class="card-button-footer pa-4 pt-0">
                <v-btn
                  rounded
                  block
                  :color="course.signedUp ? 'grey-lighten-1' : 'primary'"
                  elevation="0"
                  class="font-weight-bold"
                  @click.stop="pushToCourse(course)">
                  <v-icon left>{{ course.signedUp ? "mdi-information-outline" : "mdi-login-variant" }}</v-icon>
                  {{ course.signedUp ? "Details" : "Anmelden" }}
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-slide-y-transition>

        <v-row v-if="courseExecutions.length === 0" justify="center" class="mt-4">
          <v-col cols="12" md="10">
            <v-sheet rounded="xl" class="pa-6 text-center elevation-4" style="background-color: #f9f9f9; border: 1px solid #eee">
              <v-icon size="48" color="primary" class="mb-4">mdi-calendar-clock-outline</v-icon>
              <h3 class="text-h6 font-weight-bold text-grey-darken-3 mb-2">Bald geht's los!</h3>
              <p class="body-1 text-grey-darken-1 mb-0">
                Wir stellen gerade den detaillierten Kursplan für dieses Event zusammen. Schau bald wieder vorbei, um dir deinen Platz zu sichern!
              </p>
            </v-sheet>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <v-snackbar v-model="showCopySnackbar" :timeout="4500" :color="copySnackbarColor" bottom rounded="lg" class="mb-12">
      {{ copySnackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showCopySnackbar = false"> Schliessen </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from "../../api"
import timeService from "../../services/timeService"
import userService from "../../services/userService"
import { mapState, mapActions, mapGetters } from "vuex"

export default {
  name: "EventView",
  components: {},
  directives: {},
  data() {
    return {
      loading: true,
      timeService,
      navigationBarInitiated: false,
      event: null,
      userService,
      userID: null,
      courseExecutions: [],
      showCopySnackbar: false,
      copySnackbarText: "",
      copySnackbarColor: "success",
    }
  },
  created() {
    this.setNavigationBarText({ text: "Zurück", state: false, endPoint: "EventView" })

    this.loading = true
    if (this.userService.getUser()) {
      this.userID = this.userService.getUser()._id
    }

    api.fetchEvent(this.$route.params.id).then((data) => {
      this.event = data.data

      // VEREINFACHTES MAPPING: Wir brauchen nur 'signedUp'
      this.courseExecutions = data.courseExecutions.map((course) => {
        const signup = course.signups.find((s) => s.user === this.userID)
        return {
          ...course,
          signedUp: !!signup,
        }
      })

      this.loading = false
    })
  },
  methods: {
    ...mapActions("navigationStoreNotPersisted", ["setNavigationBarText"]),

    getCourseImage(image) {
      // 'image' ist course.course.bild
      if (!image) return "/img/einmalig/imageUploadPlaceholder.jpg"
      return "/img/kurse/" + image
    },

    pushBackToPrevRoute() {
      if (window.history.length > 2) {
        this.$router.go(-1)
      } else {
        this.$router.push("/")
      }
    },

    async shareViaWebShare() {
      // 1. Host-Namen bestimmen (Logik bleibt gleich)
      let host = ""
      if (location.host.includes("localhost")) {
        host = "well come FIT"
      }
      if (location.host.includes("wellcomefit")) {
        host = "well come FIT"
      }
      if (location.host.includes("wintifit")) {
        host = "winti FIT"
      } else if (location.host.includes("fzw")) {
        host = "FZW"
      }

      let studio
      try {
        // 2. Studio-Infos abrufen (Logik bleibt gleich)
        const studioData = await api.getUserStudio().catch((e) => {
          window.app.$root.$children[0].checkStudio()
          throw new Error("Studio-Info fehlgeschlagen") // Stoppt Ausführung bei Fehler
        })
        studio = studioData.studio
      } catch (error) {
        console.error("Fehler beim Abrufen des Studios:", error)
        // Feedback bei Fehler
        this.copySnackbarText = "Fehler: Studio-Info konnte nicht geladen werden."
        this.copySnackbarColor = "error"
        this.showCopySnackbar = true
        return
      }

      // 3. Zu kopierende Nachricht erstellen
      const text = "Hey ein super cooles Fitness Event: " + this.event.title + " im " + host + " " + studio.name + ". Kommst Du mit?"
      const url = "https://" + location.host + "/event/" + this.event._id
      // Wir fügen zwei Zeilenumbrüche hinzu, damit der Link beim Einfügen schöner aussieht
      const messageToCopy = text + "\n\n" + url

      // 4. Clipboard API prüfen (funktioniert nur auf HTTPS oder localhost)
      if (!navigator.clipboard) {
        this.copySnackbarText = "Kopieren nur in sicherer Verbindung (HTTPS) möglich."
        this.copySnackbarColor = "warning"
        this.showCopySnackbar = true
        return
      }

      // 5. In die Zwischenablage kopieren
      try {
        await navigator.clipboard.writeText(messageToCopy)

        // 6. Erfolgs-Feedback (genau wie gewünscht)
        this.copySnackbarText = "Hey! Die Nachricht mit dem Link wurde kopiert. Du kannst sie jetzt einem Freund senden."
        this.copySnackbarColor = "success"
        this.showCopySnackbar = true
      } catch (err) {
        console.error("Fehler beim Kopieren in die Zwischenablage:", err)

        // 7. Fehler-Feedback
        this.copySnackbarText = "Fehler beim Kopieren. Bitte manuell versuchen."
        this.copySnackbarColor = "error"
        this.showCopySnackbar = true
      }
    },

    pushToCourse(course) {
      console.log(this.$router.history?.current)
      this.$router.push("/kurse/" + course._id + "?event")
    },
  },
  watch: {
    navigationBarTextState(val) {
      if (this.navigationBarEndPoint === "EventView") {
        this.navigationBarInitiated ? this.pushBackToPrevRoute() : (this.navigationBarInitiated = true)
      }
    },
  },
  computed: {
    ...mapGetters("navigationStoreNotPersisted", ["navigationBarTextState", "navigationBarEndPoint"]),

    webShareApiSupported() {
      return !!navigator.share
    },
    headerImage() {
      if (!this.event || !this.event.headerImage) {
        return "/img/einmalig/imageUploadPlaceholder.jpg"
      }
      return "/img/event/" + this.event.headerImage
    },
    eventColorVariables() {
      return {
        "--event-main-color": this.event?.mainColor || "#3f4043",
        "--event-secondary-color": this.event?.secondaryColor || "#00963f",
      }
    },
    backgroundSecondaryColor() {
      return "background-color: " + (this.event?.secondaryColor || "#00963f")
    },
    shareCardGradientStyle() {
      return {
        background: `linear-gradient(90deg, var(--event-main-color) 0%, var(--event-secondary-color) 100%)`,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* 1. BASIS-LAYOUT */
.event-view-wrapper {
  // background-color: #f4f4f4;
  padding-bottom: 100px;
}

/* NEU: Header-Wrapper (für Button & Bild) */
.header-wrapper {
  // background-color: #f4f4f4;
  // padding-top: 16px;
}

/* NEU: Share-Button im normalen Flow */
.share-button-flow {
  position: relative;
  z-index: 2;
  width: fit-content;
  max-width: 90%;
  cursor: pointer;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 24px; /* Abstand zum Bild */

  .share-text {
    font-size: 0.85rem;
    letter-spacing: 0.8px;
    flex-grow: 1;
    white-space: nowrap;
  }
}

/* NEU: Freistehendes Event-Bild */
.event-header-image {
  background-color: #eee;
  /* v-img's rounded-xl und elevation-6 erledigen den Rest */
  //  border: 1px solid rgba(0, 0, 0, 0.05); /* Dezenter Border */
}

/* NEU: Content-Sektion ohne Überlappung */
.content-section {
  //background-color: #f4f4f4;
  position: relative;
  z-index: 1;
  padding-top: 0; /* Kein Padding oben mehr nötig */
}

/* Container für Desktop-Breite */
.content-container {
  max-width: 700px;
}

.section-title-pill {
  display: inline-block;
  background-color: #ffffff;
  padding: 12px 28px;
  border-radius: 30px; /* Pillen-Form */
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08); /* Dezenter Schatten */
  font-size: 1.3rem; /* Etwas kleiner, da die Pille mehr Raum einnimmt */
  letter-spacing: 0.8px;
  /* Nutzt die Event-Hauptfarbe für den Text, mit Fallback */
  color: var(--event-main-color, #3f4043);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

/*
===================================================================
✨ 2. NEUE KOMPAKTE KARTEN-STYLES
===================================================================
*/
.course-card {
  background-color: #ffffff;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  /* NEU: Höhe ist auto, kein flex-grow. Löst "Weissraum"-Problem. */
  height: auto;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15) !important;
  }

  .v-image {
    position: relative;
  }

  /* Datums-Badge (kompakt) */
  .date-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(10px);
    border-radius: 10px;
    padding: 4px 10px;
    text-align: center;
    line-height: 1.1;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .day {
      display: block;
      font-size: 1.3rem;
      font-weight: 700;
      color: #333;
    }
    .month {
      display: block;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-top: 1px;
    }
  }

  .card-content {
    /* Einfaches Padding, kein flex-grow mehr */
  }

  .course-title {
    font-size: 1.3rem;
    line-height: 1.25;
    font-weight: 700 !important;
  }

  /* NEUES ZEIT/DAUER-STYLING */
  .info-pills {
    display: flex;
    flex-wrap: wrap; /* Falls auf kleinem Screen kein Platz */
    gap: 8px; /* Abstand zwischen den Pillen */
  }

  .info-pill {
    display: flex;
    align-items: center;
    padding: 4px 10px;
    background-color: #f1f3f5; /* Helles Grau */
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;

    .v-icon {
      font-size: 1rem;
      margin-right: 6px;
      color: #555;
    }
  }

  /* NEU: Button-Footer (ersetzt v-card-actions) */
  .card-button-footer {
    /* Hält den Button unten, aber kompakt */
  }
}
</style>
